import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import api from "../../services/api";
import AdminPageHeader from "../../components/admin/AdminPageHeader";

interface EmailLog {
  id: string;
  to_email: string;
  subject: string;
  status: "sent" | "failed" | "pending";
  error: string | null;
  message_id: string | null;
  template: string | null;
  metadata: Record<string, any> | null;
  created_at: string;
  sent_at: string | null;
}

interface EmailLogDetail extends EmailLog {
  body_html: string;
  body_text: string | null;
}

interface EmailStats {
  byStatus: { sent: number; failed: number; pending: number; total: number };
  byTemplate: { template: string; count: number }[];
  recent: { sent: number; failed: number };
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export default function EmailLogs() {
  const { t } = useTranslation();
  const [logs, setLogs] = useState<EmailLog[]>([]);
  const [stats, setStats] = useState<EmailStats | null>(null);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [templateFilter, setTemplateFilter] = useState<string>("");
  const [search, setSearch] = useState("");
  const [selectedLog, setSelectedLog] = useState<EmailLogDetail | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [resending, setResending] = useState<string | null>(null);

  useEffect(() => {
    loadStats();
  }, []);

  useEffect(() => {
    loadLogs();
  }, [pagination.page, statusFilter, templateFilter, search]);

  const loadStats = async () => {
    try {
      const { data } = await api.get("/email-logs/stats");
      setStats(data);
    } catch (error) {
      console.error("Failed to load email stats:", error);
    }
  };

  const loadLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
      });
      if (statusFilter) params.append("status", statusFilter);
      if (templateFilter) params.append("template", templateFilter);
      if (search) params.append("search", search);

      const { data } = await api.get(`/email-logs?${params}`);
      setLogs(data.logs);
      setPagination(data.pagination);
    } catch (error) {
      console.error("Failed to load email logs:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadLogDetail = async (id: string) => {
    try {
      const { data } = await api.get(`/email-logs/${id}`);
      setSelectedLog(data);
      setShowModal(true);
    } catch (error) {
      console.error("Failed to load email detail:", error);
    }
  };

  const handleResend = async (id: string) => {
    try {
      setResending(id);
      await api.post(`/email-logs/${id}/resend`);
      loadLogs();
      loadStats();
    } catch (error) {
      console.error("Failed to resend email:", error);
    } finally {
      setResending(null);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "sent":
        return (
          <span className="px-2 py-1 text-xs font-medium text-emerald-700 bg-emerald-100 rounded-full">
            Trimis
          </span>
        );
      case "failed":
        return (
          <span className="px-2 py-1 text-xs font-medium text-red-700 bg-red-100 rounded-full">
            Eroare
          </span>
        );
      case "pending":
        return (
          <span className="px-2 py-1 text-xs font-medium text-amber-700 bg-amber-100 rounded-full">
            În așteptare
          </span>
        );
      default:
        return null;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("ro-RO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const uniqueTemplates = stats?.byTemplate.map((t) => t.template).filter(Boolean) || [];

  return (
    <div className="p-6 space-y-6 mx-auto max-w-[1500px]">
      {/* Header */}
      <AdminPageHeader
        eyebrow="Comunicare"
        title="Jurnal emailuri"
        subtitle="Monitorizare emailuri trimise prin platformă"
        icon={
          <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        }
      />

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-slate-500">Total emailuri</p>
                <p className="text-2xl font-bold text-slate-800">{stats.byStatus.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-slate-500">Trimise cu succes</p>
                <p className="text-2xl font-bold text-emerald-600">{stats.byStatus.sent}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-slate-500">Erori</p>
                <p className="text-2xl font-bold text-red-600">{stats.byStatus.failed}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
                <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-slate-500">Ultimele 7 zile</p>
                <p className="text-2xl font-bold text-indigo-600">{stats.recent.sent + stats.recent.failed}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-slate-600 mb-1">Caută</label>
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPagination((p) => ({ ...p, page: 1 }));
              }}
              placeholder="Email sau subiect..."
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPagination((p) => ({ ...p, page: 1 }));
              }}
              className="px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Toate</option>
              <option value="sent">Trimise</option>
              <option value="failed">Erori</option>
              <option value="pending">În așteptare</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Template</label>
            <select
              value={templateFilter}
              onChange={(e) => {
                setTemplateFilter(e.target.value);
                setPagination((p) => ({ ...p, page: 1 }));
              }}
              className="px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Toate</option>
              {uniqueTemplates.map((template) => (
                <option key={template} value={template || ""}>
                  {template}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => {
                loadLogs();
                loadStats();
              }}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Reîncarcă
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full mx-auto" />
            <p className="mt-4 text-slate-500">Se încarcă...</p>
          </div>
        ) : logs.length === 0 ? (
          <div className="p-8 text-center">
            <svg className="w-16 h-16 text-slate-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <p className="text-slate-500">Nu există emailuri</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Destinatar
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Subiect
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Template
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Data
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Acțiuni
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {logs.map((log) => (
                  <tr key={log.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3">
                      <span className="text-sm text-slate-800">{log.to_email}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-slate-600 truncate block max-w-xs" title={log.subject}>
                        {log.subject}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">
                        {log.template || "unknown"}
                      </span>
                    </td>
                    <td className="px-4 py-3">{getStatusBadge(log.status)}</td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-slate-500">{formatDate(log.created_at)}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => loadLogDetail(log.id)}
                          className="px-3 py-1 text-sm text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                        >
                          Vezi
                        </button>
                        {log.status === "failed" && (
                          <button
                            onClick={() => handleResend(log.id)}
                            disabled={resending === log.id}
                            className="px-3 py-1 text-sm text-amber-600 hover:bg-amber-50 rounded-lg transition-colors disabled:opacity-50"
                          >
                            {resending === log.id ? "..." : "Retrimite"}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between">
            <p className="text-sm text-slate-500">
              {pagination.total} emailuri total
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))}
                disabled={pagination.page === 1}
                className="px-3 py-1 text-sm text-slate-600 hover:bg-slate-100 rounded-lg disabled:opacity-50"
              >
                Înapoi
              </button>
              <span className="text-sm text-slate-600">
                Pagina {pagination.page} din {pagination.totalPages}
              </span>
              <button
                onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}
                disabled={pagination.page === pagination.totalPages}
                className="px-3 py-1 text-sm text-slate-600 hover:bg-slate-100 rounded-lg disabled:opacity-50"
              >
                Înainte
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && selectedLog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-800">Detalii Email</h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  setSelectedLog(null);
                }}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {/* Email Info */}
              <div className="space-y-4 mb-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Destinatar</p>
                    <p className="font-medium text-slate-800">{selectedLog.to_email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Status</p>
                    <p>{getStatusBadge(selectedLog.status)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Template</p>
                    <p className="font-medium text-slate-800">{selectedLog.template || "unknown"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Data</p>
                    <p className="font-medium text-slate-800">{formatDate(selectedLog.created_at)}</p>
                  </div>
                </div>

                {selectedLog.error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-600 font-medium">Eroare:</p>
                    <p className="text-sm text-red-700">{selectedLog.error}</p>
                  </div>
                )}

                {selectedLog.message_id && (
                  <div>
                    <p className="text-sm text-slate-500">Message ID</p>
                    <p className="font-mono text-sm text-slate-600">{selectedLog.message_id}</p>
                  </div>
                )}
              </div>

              {/* Subject */}
              <div className="mb-4">
                <p className="text-sm text-slate-500 mb-1">Subiect</p>
                <p className="font-medium text-slate-800 bg-slate-50 px-3 py-2 rounded-lg">
                  {selectedLog.subject}
                </p>
              </div>

              {/* Body Preview */}
              <div>
                <p className="text-sm text-slate-500 mb-2">Conținut Email</p>
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <iframe
                    srcDoc={selectedLog.body_html}
                    title="Previzualizare email"
                    className="w-full h-96 bg-white"
                    sandbox=""
                  />
                </div>
              </div>

              {/* Metadata */}
              {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                <div className="mt-4">
                  <p className="text-sm text-slate-500 mb-2">Metadata</p>
                  <pre className="bg-slate-50 p-3 rounded-lg text-xs text-slate-600 overflow-x-auto">
                    {JSON.stringify(selectedLog.metadata, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
